import { authService } from '@/services/auth/authService';

type Listener = () => void;

export interface MruStoreOptions<T> {
  /** localStorage key prefix; the tenant, then the list name (if any), follow it. */
  prefix: string;
  /** Entries kept per list, newest first. */
  max: number;
  /** Guards what comes back out of localStorage — an older app version may have written it. */
  isEntry: (x: unknown) => x is T;
  /** The same entry, for dedup-and-promote. */
  isSame: (a: T, b: T) => boolean;
  /** The entry stored when `next` is recorded over an existing one (default: `next` as is). */
  merge?: (next: T, existing: T | undefined) => T;
}

export interface MruStore<T> {
  /** The list, newest first. A stable reference between changes, as `useSyncExternalStore` needs. */
  get(list?: string): T[];
  /** Move `entry` to the front, deduplicated and capped. Emits only when the list changed. */
  record(entry: T, list?: string): void;
  clear(list?: string): void;
  subscribe(listener: Listener): () => void;
}

/**
 * Most-recently-used lists in localStorage, one per tenant and optional list name.
 *
 * Keyed per tenant: one browser serves several tenants, and a history listing another tenant's
 * records or values would leak across a boundary the rest of the app enforces. Switching tenant
 * (possible without a reload) therefore switches history rather than merging it.
 *
 * Each list is cached with the raw string it was parsed from and re-parsed only when localStorage
 * holds a different one, so readers get the same array until something actually changed — also
 * when many lists are read in one render.
 */
export function createMruStore<T>({ prefix, max, isEntry, isSame, merge }: MruStoreOptions<T>): MruStore<T> {
  const cache = new Map<string, { raw: string | null; entries: T[] }>();
  const listeners = new Set<Listener>();

  const storageKey = (list?: string) =>
    `${prefix}.${authService.getTenantId() ?? 'host'}${list ? `.${list}` : ''}`;

  function readRaw(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null; // localStorage may be unavailable (private mode); the list is then in memory only.
    }
  }

  function parse(raw: string | null): T[] {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(isEntry) : [];
    } catch {
      return [];
    }
  }

  function load(key: string): T[] {
    const raw = readRaw(key);
    const cached = cache.get(key);
    if (cached && cached.raw === raw) return cached.entries;
    const entries = parse(raw);
    cache.set(key, { raw, entries });
    return entries;
  }

  function write(key: string, entries: T[]): void {
    const raw = JSON.stringify(entries);
    try {
      localStorage.setItem(key, raw);
      cache.set(key, { raw, entries });
    } catch {
      // Quota or private mode: keep the list in memory, cached against what storage still holds.
      cache.set(key, { raw: readRaw(key), entries });
    }
    for (const listener of listeners) listener();
  }

  return {
    get: (list) => load(storageKey(list)),

    record(entry, list) {
      const key = storageKey(list);
      const current = load(key);
      const existing = current.find((x) => isSame(x, entry));
      const next = merge ? merge(entry, existing) : entry;
      // Nothing changed — do not emit, or every re-record of the front entry would notify.
      if (existing && current[0] === existing && JSON.stringify(existing) === JSON.stringify(next)) return;
      write(key, [next, ...current.filter((x) => !isSame(x, entry))].slice(0, max));
    },

    clear(list) {
      const key = storageKey(list);
      if (load(key).length === 0) return;
      write(key, []);
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
